import { Checkbox } from "@mui/material";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { setRef } from '@mui/material/utils';
import { createContext, forwardRef, HTMLAttributes, Key, KeyboardEvent, MutableRefObject, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { List, ListImperativeAPI, ListProps, useDynamicRowHeight } from "react-window";

const ROW_HEIGHT = 44;
const VISIBLE_ROWS = 8;
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// Like MUI's virtualization example, renderOption supplies data, not JSX.
// Only the visible rows create the actual option and Checkbox components.
export type VirtualizedAutocompleteOption = {
  props: HTMLAttributes<HTMLLIElement> & { key: Key; 'aria-disabled'?: boolean | 'true' | 'false' };
  label: string;
  selected: boolean;
  showCheckbox?: boolean;
};

export type VirtualizedAutocompleteNavigation = {
  prepareKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  highlight: (index: number, reason: 'keyboard' | 'mouse' | 'touch') => void;
};

export const VirtualizedAutocompleteContext = createContext<{
  navigationRef: MutableRefObject<VirtualizedAutocompleteNavigation | null>;
  inputValue: string;
  multiline?: boolean;
} | null>(null);

type OptionRowProps = Parameters<ListProps<{ items: VirtualizedAutocompleteOption[] }>['rowComponent']>[0];

function OptionRow({ index, style, items }: OptionRowProps) {
  const { multiline } = useContext(VirtualizedAutocompleteContext)!;
  const { props, label, selected } = items[index];
  const { key, ...optionProps } = props;
  const top = style.transform ? parseFloat(String(style.transform).replace('translateY(', '')) : style.top;

  return (
    <li
      key={key}
      {...optionProps}
      aria-posinset={index + 1}
      aria-setsize={items.length}
      // MUI scrolls using offsetTop, which does not include CSS transforms.
      style={{ ...style, transform: undefined, top, minHeight: ROW_HEIGHT, display: 'flex', alignItems: 'center', boxSizing: 'border-box' }}>
      {items[index].showCheckbox !== false && <Checkbox
        size="small"
        icon={icon}
        checkedIcon={checkedIcon}
        checked={selected}
        tabIndex={-1}
        disableRipple
        slotProps={{ input: { 'aria-hidden': true } }}
        sx={{ marginRight: 1, flexShrink: 0, pointerEvents: 'none' }}
      />}
      <span title={label} style={multiline
        ? { whiteSpace: 'pre-line', overflowWrap: 'anywhere', minWidth: 0 }
        : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
        {label}
      </span>
    </li>
  );
}

export const VirtualizedAutocompleteListbox = forwardRef<HTMLDivElement, HTMLAttributes<HTMLElement>>(
  function VirtualizedAutocompleteListbox({ children, style, ...props }, forwardedRef) {
    const context = useContext(VirtualizedAutocompleteContext)!;
    const items = children as unknown as VirtualizedAutocompleteOption[];
    const dynamicRowHeight = useDynamicRowHeight({ defaultRowHeight: ROW_HEIGHT, key: context.inputValue });
    const listRef = useRef<ListImperativeAPI | null>(null);
    const highlightRef = useRef({ index: -1, reason: 'keyboard' });
    const [navigationIndex, setNavigationIndex] = useState(-1);
    const [renderedRange, setRenderedRange] = useState({ startIndex: 0, stopIndex: -1 });

    const attachList = useCallback((api: ListImperativeAPI | null) => {
      listRef.current = api;
      setRef(forwardedRef, api?.element ?? null);
    }, [forwardedRef]);

    useEffect(() => {
      setNavigationIndex(-1);
      highlightRef.current.index = -1;
      listRef.current?.scrollToRow({ index: 0, align: 'start' });
    }, [context.inputValue]);

    useLayoutEffect(() => {
      const navigation: VirtualizedAutocompleteNavigation = {
        highlight: (index, reason) => {
          highlightRef.current = { index, reason };
          if (reason === 'keyboard' && index >= 0 && index < items.length) {
            listRef.current?.scrollToRow({ index, align: 'auto' });
          }
        },
        prepareKeyDown: event => {
          if (event.nativeEvent.isComposing || event.which === 229 || !items.length) return;
          const input = event.target as HTMLInputElement;
          const activeId = input.getAttribute('aria-activedescendant');
          const currentIndex = activeId ? Number(activeId.slice(activeId.lastIndexOf('-') + 1)) : -1;
          let index: number;
          let direction = 1;

          switch (event.key) {
            case 'Home': index = 0; break;
            case 'End': index = items.length - 1; direction = -1; break;
            case 'ArrowDown': index = currentIndex + 1; break;
            case 'ArrowUp': index = currentIndex === -1 ? items.length - 1 : currentIndex - 1; direction = -1; break;
            case 'PageDown': index = currentIndex + 5; break;
            case 'PageUp': index = currentIndex - 5; direction = -1; break;
            default: return;
          }

          index = Math.max(0, Math.min(index, items.length - 1));
          // MUI skips disabled options with wraparound, even with disableListWrap.
          for (let checked = 0; checked < items.length; checked++) {
            const disabled = items[index].props['aria-disabled'];
            if (disabled !== true && disabled !== 'true') {
              // MUI validates keyboard targets against the DOM. Mount an offscreen
              // target before its native handler runs (Home/End and disabled gaps).
              const targetIndex = index;
              flushSync(() => setNavigationIndex(targetIndex));
              return;
            }
            index = (index + direction + items.length) % items.length;
          }
        },
      };
      context.navigationRef.current = navigation;
      return () => {
        if (context.navigationRef.current === navigation) context.navigationRef.current = null;
      };
    }, [context.navigationRef, items]);

    useLayoutEffect(() => {
      // Preserve MUI's highlight classes when a pinned keyboard target is
      // replaced by its normal virtual row after scrolling.
      const list = listRef.current?.element;
      const { index, reason } = highlightRef.current;
      const option = list?.querySelector(`[data-option-index="${index}"]`);
      if (option) {
        option.classList.add('Mui-focused');
        option.classList.toggle('Mui-focusVisible', reason === 'keyboard');
      }
    }, [items, renderedRange, navigationIndex]);

    const renderNavigationRow = navigationIndex >= 0 && navigationIndex < items.length
      && (navigationIndex < renderedRange.startIndex || navigationIndex > renderedRange.stopIndex);
    const estimatedHeight = (index: number) => context.multiline
      ? dynamicRowHeight.getRowHeight(index) ?? dynamicRowHeight.getAverageRowHeight()
      : ROW_HEIGHT;
    const height = Math.min(VISIBLE_ROWS * ROW_HEIGHT,
      Array.from({ length: Math.min(items.length, VISIBLE_ROWS) }, (_, index) => estimatedHeight(index))
        .reduce((total, rowHeight) => total + rowHeight, 0));
    // The pinned keyboard target uses the same measured/estimated offsets as List.
    const navigationTop = renderNavigationRow
      ? Array.from({ length: navigationIndex }, (_, index) => estimatedHeight(index)).reduce((total, rowHeight) => total + rowHeight, 0)
      : 0;

    return (
      <List
        {...props}
        listRef={attachList}
        tagName="ul"
        role="listbox"
        rowComponent={OptionRow}
        rowProps={{ items }}
        rowCount={items.length}
        rowHeight={context.multiline ? dynamicRowHeight : ROW_HEIGHT}
        defaultHeight={height}
        overscanCount={5}
        onRowsRendered={(_, range) => setRenderedRange(previous =>
          previous.startIndex === range.startIndex && previous.stopIndex === range.stopIndex ? previous : range)}
        style={{ ...style, height, maxHeight: height, padding: 0, margin: 0, position: 'relative' }}>
        {renderNavigationRow && (
          <OptionRow
            index={navigationIndex}
            items={items}
            ariaAttributes={{ role: 'listitem', 'aria-posinset': navigationIndex + 1, 'aria-setsize': items.length }}
            style={{ position: 'absolute', top: navigationTop, height: context.multiline ? undefined : ROW_HEIGHT, width: '100%' }}
          />
        )}
      </List>
    );
  }
);
